import React, { useMemo } from 'react'
import { Redirect, useLocation } from 'react-router'
import { Route, Switch } from 'react-router-dom'
import routes from 'constants/router'
import findMatchedRoutes from 'utils/findMatchedRoutes'
import { ROUTES } from 'constants/routes'
import LAYOUTS_MAP from 'layouts'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useAuthState } from 'modules/Auth/hooks/useAuthState'
import RouteGuard from 'modules/Auth/RouteGuard'
import RouteModuleRenderer from 'modules/Auth/RouteModuleRenderer'
import { AUTHORIZED_SHELL_I18N_NAMESPACES } from 'i18nProduct'
import { isPrivateAccess } from 'utils/routeAccess'

const NO_SHELL_I18N_NAMESPACES = []

const groupRoutesByLayout = (routeList) => {
  const groups = {}

  routeList.forEach((route) => {
    const layoutKey = route.layout ?? 'main'

    if (!groups[layoutKey]) {
      groups[layoutKey] = []
    }
    groups[layoutKey].push(route)
  })

  return groups
}

const AppRouter = () => {
  const pathGen = useGeneratePathI18n()
  const location = useLocation()
  const { user, isAuthenticated } = useAuthState()

  const groupedRoutes = useMemo(() => groupRoutesByLayout(routes), [])

  const matchedRoutes = useMemo(
    () => findMatchedRoutes(routes, location.pathname),
    [location.pathname],
  )

  const isPrivateRoute = useMemo(
    () => isPrivateAccess(matchedRoutes.at(-1)?.access),
    [matchedRoutes],
  )

  const shellI18nNamespaces = useMemo(
    () => (user?.id && isPrivateRoute ? AUTHORIZED_SHELL_I18N_NAMESPACES : NO_SHELL_I18N_NAMESPACES),
    [user?.id, isPrivateRoute],
  )

  return (
    <Switch>
      {Object.entries(groupedRoutes)
        .reverse()
        .map(([layoutKey, layoutRoutes]) => {
          const LayoutComponent = LAYOUTS_MAP[layoutKey ?? 'main']
          const paths = layoutRoutes.flatMap(({ path }) => path)

          return (
            <Route
              path={paths}
              key={layoutKey}
              render={() => (
                <LayoutComponent>
                  <Switch>
                    {layoutRoutes.map((route) => {
                      const { component: RouteComponent = null, key, exact, path } = route

                      return (
                        <Route
                          path={path}
                          key={'route-' + key}
                          exact={exact}
                          render={() => (
                            <RouteGuard route={route}>
                              <RouteModuleRenderer
                                routeKey={key}
                                routePath={path}
                                RouteComponent={RouteComponent}
                                shellI18nNamespaces={shellI18nNamespaces}
                              />
                            </RouteGuard>
                          )}
                        />
                      )
                    })}
                  </Switch>
                </LayoutComponent>
              )}
            />
          )
        })}
      <Route
        key={'no-match'}
        path={'*'}
        render={() => (
          <Redirect
            to={
              isAuthenticated ? pathGen(ROUTES.SERVICES) : pathGen(ROUTES.AUTH_LOGIN)
            }
          />
        )}
      />
    </Switch>
  )
}

export default AppRouter
